import { FC, useMemo } from "react";
import { Box, Stack, Typography } from "@mui/material";
import type { CustomHeaderProps } from "ag-grid-react";
import { ResourceExplorerGridDataWithId } from "@/store/resource-explorer/utils/types/resourceExplorerDataGrid";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { useTotalInHeaderCalculator } from "@/components/pages/common/ag-grid/utils/hooks/useTotalInHeaderCalculator.hook";
import { money } from "@/utils/numeral/money";
import { ResourceExplorerCostsDateDetails } from "@/services/cloudchipr.api";
import { formatDate } from "@/utils/helpers/date-time/datetime-format";
import { TooltipText } from "@/components/common/TooltipText";

interface InnerHeaderComponentParamsProps {
  approximate?: boolean;
  dates?: ResourceExplorerCostsDateDetails;
}

export const BillingExplorerWidgetCostHeaderCell: FC<
  CustomHeaderProps<ResourceExplorerGridDataWithId>
> = ({ innerHeaderComponentParams, ...props }) => {
  const { approximate, dates }: InnerHeaderComponentParamsProps =
    innerHeaderComponentParams ?? {};
  const colId = props.column.getColId() as keyof ResourceExplorerGridDataWithId;

  const toDate = formatDate(dates?.to_date ?? "", { type: "date" });
  const fromDate = formatDate(dates?.from_date ?? "", {
    type: "date",
  });

  const total = useTotalInHeaderCalculator<ResourceExplorerGridDataWithId>(
    props.api,
    (data) => data[colId],
    "sum",
  );
  const cost = money(total);

  const titleComponent = useMemo(
    () => (
      <Box>
        {props.displayName} <br /> from{" "}
        <Typography fontWeight="medium" fontSize="inherit" component="span">
          {fromDate}
        </Typography>{" "}
        to{" "}
        <Typography fontWeight="medium" fontSize="inherit" component="span">
          {toDate}
        </Typography>
      </Box>
    ),
    [fromDate, toDate, props.displayName],
  );

  return (
    <Stack width="100%" position="relative" alignItems="flex-end">
      <TooltipText
        width="100%"
        variant="body2"
        fontWeight="medium"
        sx={ellipsisStyles}
        tooltipPlacement="top"
        tooltipTitle={fromDate && toDate ? titleComponent : ""}
      >
        {props.displayName}
      </TooltipText>

      <TypographyWithTooltip
        fontSize={10}
        variant="caption"
        title={cost !== "N/A" && approximate ? `~${cost}` : cost}
      />
    </Stack>
  );
};

const ellipsisStyles = {
  textOverflow: "ellipsis",
  overflow: "hidden",
  whiteSpace: "nowrap",
};
